import { useCallback, useState } from 'react';
import { useApiClient } from '../../utils/apiClient';
import { responseToBlob } from '../../utils/pdfUtils';

export const useDocumentDownload = () => {
  const { apiGet } = useApiClient();
  const [downloading, setDownloading] = useState(false);

  const buildEndpointUrl = useCallback((documentId, useSignedPdf) => {
    const docsignParams = {
      document_id: String(documentId),
    };
    const qs = new URLSearchParams(docsignParams).toString();

    return useSignedPdf
      ? `/docsign/signed_doc_content?${qs}`
      : `/docsign/doc_content?${qs}`;
  }, []);

  const downloadDocument = useCallback(async (document, useSignedPdf = false) => {
    if (!document || !document.id) {
      throw new Error('Document ID is required');
    }

    setDownloading(true);
    try {
      const endpoint = buildEndpointUrl(document.id, useSignedPdf);
      const response = await apiGet(endpoint);

      if (!response.ok) {
        throw new Error(`Failed to download document: ${response.status} ${response.statusText}`);
      }

      const { blob } = await responseToBlob(response);

      if (!blob || blob.size === 0) {
        throw new Error('Failed to create PDF blob: empty or invalid blob');
      }

      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      const documentName = document.name || document.meta?.display_name || document.meta?.name || 'document';
      const sanitizedName = documentName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const suffix = useSignedPdf ? '_signed' : '_unsigned';
      a.download = `${sanitizedName}${suffix}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    } finally {
      setDownloading(false);
    }
  }, [apiGet, buildEndpointUrl]);

  const downloadUnsignedDocument = useCallback(async (document) => {
    return downloadDocument(document, false);
  }, [downloadDocument]);

  const downloadSignedDocument = useCallback(async (document) => {
    return downloadDocument(document, true);
  }, [downloadDocument]);

  return {
    downloadDocument,
    downloadUnsignedDocument,
    downloadSignedDocument,
    downloading,
  };
};
